import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { country as countryList } from "../../../../utilities/country";
import { submitSuperAffiliateApplication } from "../../../../store/reducers/affiliateReducer";

export const PLATFORMS = [
  "TikTok",
  "Instagram",
  "X",
  "YouTube",
  "Kick",
  "Facebook",
];

// Each social link field maps to its own payload key.
const SOCIAL_LINKS = [
  { key: "tiktok_url", label: "Please submit a link to your TikTok" },
  { key: "instagram_url", label: "Please submit a link to your Instagram" },
  { key: "x_url", label: "Please submit a link to your X" },
  { key: "youtube_url", label: "Please submit a link to your YouTube" },
  { key: "kick_url", label: "Please submit a link to your Kick" },
  { key: "facebook_url", label: "Please submit a link to your Facebook" },
];

const REQUIRED_FIELDS = [
  { key: "full_name", label: "Full Name" },
  { key: "email", label: "Email" },
  { key: "country", label: "Where are you from?" },
  { key: "content_description", label: "How would you describe your content?" },
  {
    key: "primary_platform",
    label: "Which is the most popular platform you post on?",
  },
  { key: "audience_location", label: "Where is your audience based?" },
];

const EMPTY_FORM = {
  full_name: "",
  email: "",
  country_code: "",
  phone_number: "",
  country: "",
  content_description: "",
  primary_platform: "",
  audience_location: "",
  follower_count: "",
  tiktok_url: "",
  instagram_url: "",
  x_url: "",
  youtube_url: "",
  kick_url: "",
  facebook_url: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Dialling codes, deduped by code the way the profile form does it. Codes such
// as "1-684" are stored without the dash.
const DIAL_CODES = (() => {
  const seen = new Set();
  return countryList
    .map((item) => ({
      code: String(item.code || "").replace(/-/g, ""),
      name: item.country,
    }))
    .filter(({ code }) => {
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
})();

const ApplicationModal = ({ open, onClose, onSubmitted }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Whatever the profile already knows, so the applicant does not retype it.
  // Email casing differs across the codebase, so both spellings are tried.
  const prefill = useMemo(
    () => ({
      email: user?.Email || user?.email || "",
      full_name:
        [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
        user?.full_name ||
        "",
      country_code: String(user?.country_code || "").replace(/[+-]/g, ""),
      phone_number: String(user?.Contact_number || "").replace(/\D/g, ""),
      country: user?.Country || "",
    }),
    [
      user?.Email,
      user?.email,
      user?.first_name,
      user?.last_name,
      user?.full_name,
      user?.country_code,
      user?.Contact_number,
      user?.Country,
    ]
  );

  // Fills blanks only, so it never overwrites what the user is typing - the
  // profile can resolve after the modal is already open.
  useEffect(() => {
    if (!open) return;
    setForm((prev) => {
      const next = { ...prev };
      Object.entries(prefill).forEach(([key, value]) => {
        if (value && !next[key]) next[key] = value;
      });
      return next;
    });
  }, [open, prefill]);

  if (!open) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear the field's error as soon as the user edits it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const validate = () => {
    const nextErrors = {};
    REQUIRED_FIELDS.forEach(({ key, label }) => {
      if (!String(form[key] || "").trim())
        nextErrors[key] = `${label} is required`;
    });
    if (form.email && !EMAIL_PATTERN.test(form.email.trim()))
      nextErrors.email = "Enter a valid email address";
    if (!form.country_code) nextErrors.country_code = "Select a country code";
    if (!String(form.phone_number || "").trim())
      nextErrors.phone_number = "WhatsApp Number is required";
    else if (String(form.phone_number).replace(/\D/g, "").length < 6)
      nextErrors.phone_number = "Enter a valid number";
    if (form.follower_count && Number.isNaN(Number(form.follower_count)))
      nextErrors.follower_count = "Enter a number";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !validate()) return;

    // Drop the optional fields the user left blank rather than sending "".
    const payload = Object.entries(form).reduce((acc, [key, value]) => {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (trimmed !== "") acc[key] = trimmed;
      return acc;
    }, {});
    // The API stores one dialled number, e.g. "+447700900123", so the code and
    // the number are merged and country_code is not sent on its own.
    delete payload.country_code;
    payload.phone_number = `+${form.country_code}${String(
      form.phone_number
    ).replace(/\D/g, "")}`;
    setSubmitting(true);
    try {
      const response = await dispatch(submitSuperAffiliateApplication(payload));
      toast.success("Application submitted. We will review it shortly.");
      setForm(EMPTY_FORM);
      onSubmitted?.(response);
      onClose?.();
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error;
      // Field-level errors come back keyed by field name.
      const fieldErrors = error?.response?.data;
      if (fieldErrors && typeof fieldErrors === "object" && !detail) {
        const mapped = {};
        Object.entries(fieldErrors).forEach(([key, value]) => {
          if (key in EMPTY_FORM)
            mapped[key] = Array.isArray(value) ? value[0] : String(value);
        });
        if (Object.keys(mapped).length) setErrors(mapped);
      }
      toast.error(detail || "Could not submit the application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (key, label, { required, type = "text", ...rest } = {}) => (
    <div className="sa-field" key={key}>
      <label htmlFor={`sa-${key}`}>
        {label}
        {required && <span className="sa-required"> *</span>}
      </label>
      <input
        id={`sa-${key}`}
        type={type}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
        className={errors[key] ? "has-error" : ""}
        {...rest}
      />
      {errors[key] && <small className="sa-error">{errors[key]}</small>}
    </div>
  );

  return (
    <div
      className="sa-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose?.();
      }}
    >
      <div
        className="sa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-modal-title"
      >
        <div className="sa-modal-head">
          <h3 id="sa-modal-title">Super Affiliate Application</h3>
          <button
            type="button"
            className="sa-close"
            onClick={() => !submitting && onClose?.()}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <form className="sa-modal-body" onSubmit={handleSubmit} noValidate>
          {renderInput("full_name", "Full Name", { required: true })}
          {renderInput("email", "Email", { required: true, type: "email" })}
          <div className="sa-field">
            <label htmlFor="sa-phone_number">
              WhatsApp Number
              <span className="sa-required"> *</span>
            </label>
            <div className="sa-phone-row">
              <select
                aria-label="Country dialling code"
                value={form.country_code}
                onChange={(e) => setField("country_code", e.target.value)}
                className={errors.country_code ? "has-error" : ""}
              >
                <option value="">Code</option>
                {DIAL_CODES.map(({ code, name }) => (
                  <option key={`${code}-${name}`} value={code}>
                    +{code} {name}
                  </option>
                ))}
              </select>
              <input
                id="sa-phone_number"
                type="tel"
                inputMode="tel"
                value={form.phone_number}
                placeholder="7700900123"
                onChange={(e) =>
                  setField("phone_number", e.target.value.replace(/\D/g, ""))
                }
                className={errors.phone_number ? "has-error" : ""}
              />
            </div>
            {(errors.country_code || errors.phone_number) && (
              <small className="sa-error">
                {errors.country_code || errors.phone_number}
              </small>
            )}
          </div>
          {renderInput("country", "Where are you from?", { required: true })}

          <div className="sa-field sa-field-full">
            <label htmlFor="sa-content_description">
              How would you describe your content?
              <span className="sa-required"> *</span>
            </label>
            <textarea
              id="sa-content_description"
              rows={3}
              value={form.content_description}
              onChange={(e) => setField("content_description", e.target.value)}
              className={errors.content_description ? "has-error" : ""}
            />
            {errors.content_description && (
              <small className="sa-error">{errors.content_description}</small>
            )}
          </div>

          <div className="sa-field">
            <label htmlFor="sa-primary_platform">
              Which is the most popular platform you post on?
              <span className="sa-required"> *</span>
            </label>
            <select
              id="sa-primary_platform"
              value={form.primary_platform}
              onChange={(e) => setField("primary_platform", e.target.value)}
              className={errors.primary_platform ? "has-error" : ""}
            >
              <option value="">Select a platform</option>
              {PLATFORMS.map((platform) => (
                <option key={platform} value={platform}>
                  {platform}
                </option>
              ))}
            </select>
            {errors.primary_platform && (
              <small className="sa-error">{errors.primary_platform}</small>
            )}
          </div>

          {renderInput("audience_location", "Where is your audience based?", {
            required: true,
          })}
          {renderInput(
            "follower_count",
            "How many followers do you have on your most popular platform?",
            { inputMode: "numeric" }
          )}

          <div className="sa-section-title sa-field-full">
            <h4>Social Media Links</h4>
            <p>Please also include the profiles you post on.</p>
          </div>

          {SOCIAL_LINKS.map(({ key, label }) =>
            renderInput(key, label, { type: "url", placeholder: "https://" })
          )}

          <div className="sa-modal-actions sa-field-full">
            <button
              type="button"
              className="sa-btn sa-btn-ghost"
              onClick={() => onClose?.()}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="sa-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationModal;
